import { omit } from 'lodash-es'
import { type MaybeRefOrGetter, toValue } from 'vue'

import type { IndexedDBCache } from '~/services/cache/IndexedDBCache.ts'
import { InMemoryCache } from '~/services/cache/InMemoryCache.ts'
import type { SessionCache } from '~/services/cache/SessionCache.ts'

interface Options {
  /**
   * The cache key to use to store the filters.
   * If not provided, the filters will not be cached.
   */
  cacheKey?: MaybeRefOrGetter<string>

  /**
   * The cache implementation to use for the filters.
   * @default InMemoryCache
   */
  cache?: IndexedDBCache | InMemoryCache | SessionCache

  /**
   * Keys that are omitted from the filters when checking if the filters are active.
   */
  omitKeysForActiveComparison?: string[]

  /**
   * Callback for when the cache is first loaded.
   */
  onCacheLoaded?: () => void
}

export function useFilters<T extends Record<string, unknown>>(defaultFilters: T, options: Options = {}) {
  const omitKeysForActiveComparison = options.omitKeysForActiveComparison ?? ['sort_by', 'sort_order', 'page', 'per_page', 'last_page']
  const defaults = reactive(copyObject(defaultFilters))

  const cache = options.cache ?? InMemoryCache.tags('useFilters')
  const cacheKeyOption = options.cacheKey

  async function loadFromCache() {
    const key = toValue(cacheKeyOption)
    if (!key) return

    const result = await cache.get<T | null, T>(key, copyObject(defaults) as T)
    Object.assign(filters, result)
    options.onCacheLoaded?.()
  }

  const filters = reactive({
    ...copyObject(defaultFilters),

    reset: (...fields: (keyof T)[]): void => {
      const original = copyObject(defaults)

      if (fields.length === 0) {
        Object.assign(filters, original)
      } else {
        for (const key of fields) {
          // @ts-expect-error - `original` and `form` should have the same keys.
          filters[key] = original[key]
        }
      }
    },

    toJson: (): T => {
      const json: Record<string, unknown> = {}

      for (const key in defaultFilters) {
        json[key] = filters[key]
      }

      return json as T
    },

    updateDefaults(newDefaults?: Partial<T>): void {
      if (!newDefaults) {
        Object.assign(defaults, filters.toJson())

        return
      }

      Object.assign(defaults, newDefaults)
      Object.assign(filters, copyObject(newDefaults))
    },
  })

  if (toValue(cacheKeyOption)) {
    loadFromCache().finally(() => {
      watch(
        () => filters,
        () => {
          const key = toValue(cacheKeyOption)
          if (key) cache.put(key, filters)
        },
        { deep: true, immediate: true },
      )
    })

    // Reload from cache when the key changes (for reactive keys).
    if (typeof cacheKeyOption !== 'string') {
      watch(
        () => toValue(cacheKeyOption),
        () => loadFromCache(),
      )
    }
  }

  const stringifiedFilters = computed(() => JSON.stringify(omit(filters, omitKeysForActiveComparison)))
  const stringifiedDefaultFilters = computed(() => JSON.stringify(omit(defaults, omitKeysForActiveComparison)))
  const filtersActive = computed(() => stringifiedFilters.value !== stringifiedDefaultFilters.value)

  return { filters, filtersActive, defaults }
}
